import React from "react";

// Modules
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import moment from "moment";

// UI components
import { Col, Row } from "react-bootstrap";
import { Button, Chip, Typography } from "@mui/material";
import OpenNewIcon from "@mui/icons-material/OpenInNewOutlined";
import EditIcon from "@mui/icons-material/EditOutlined";
import ShareIcon from "@mui/icons-material/ShareOutlined";
import LockIcon from "@mui/icons-material/LockOutlined";

const statusChipColor = {
  draft: "default",
  active: "success",
  closed: "error",
};

const actionIconSx = { fontSize: "0.9rem" };

export default function SalesRoomItem(props) {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const item = props.item;
  const shareLink = item.access_token
    ? `${window.location.origin}/room/${item.access_token}`
    : null;

  const copyShareLink = () => {
    if (!shareLink) {
      enqueueSnackbar("Share link not available", { variant: "warning" });
      return;
    }
    navigator.clipboard
      .writeText(shareLink)
      .then(() => {
        enqueueSnackbar(
          "Share link copied. Anyone with this link can view the room while it is active — no login required.",
          { variant: "success" }
        );
      })
      .catch(() => {
        enqueueSnackbar("Could not copy the share link", { variant: "error" });
      });
  };

  const lastActivity = item.stats?.last_activity
    ? moment.unix(item.stats.last_activity).fromNow()
    : "—";

  // One button per fixed column — must stay in sync with the listLabels
  // grid in salesRooms.js.
  const actionButton = (title, onClick, Icon, disabled = false) => (
    <Button
      variant="screenItemBtn"
      mode="default"
      title={title}
      disabled={disabled}
      onClick={onClick}
    >
      <Icon sx={actionIconSx} />
    </Button>
  );

  return (
    <Col xs={12} className="sales-room-item screen-item px-0">
      <Row className="list-item-row d-flex align-items-center py-2">
        <Col xs={1}>
          <Typography owner="client" variant="body2">
            {item.id}
          </Typography>
        </Col>
        <Col
          xs={6}
          md={5}
          lg={4}
          className="sales-room-item__title"
          role="button"
          onClick={() => navigate(`/sales-rooms/${item.id}`)}
        >
          <Typography
            className="list-item-title"
            owner="client"
            variant="subtitle1"
            lh="semi-wide"
            weight="medium"
          >
            {item.title}
          </Typography>
          {item.field_company && (
            <Typography
              className="list-subtitle"
              owner="client"
              variant="body2"
            >
              {item.field_company}
            </Typography>
          )}
        </Col>
        <Col
          xs={2}
          lg={1}
          className="d-flex justify-content-center align-items-center gap-1"
        >
          {item.is_template ? (
            <Chip
              size="small"
              label="template"
              color="info"
              className="sales-room-item__status"
            />
          ) : (
            <Chip
              size="small"
              label={item.room_status}
              color={statusChipColor[item.room_status] || "default"}
              className="sales-room-item__status"
            />
          )}
          {(item.access_mode === "password" ||
            item.access_mode === "restricted" ||
            item.has_password) && (
            <LockIcon sx={{ fontSize: "0.9rem", opacity: 0.6 }} />
          )}
        </Col>
        <Col
          md={1}
          className="d-none d-md-flex justify-content-center"
          title={`${item.stats?.unique_visitors ?? 0} unique ${
            (item.stats?.unique_visitors ?? 0) === 1 ? "visitor" : "visitors"
          } · ${item.stats?.total_opens ?? 0} total opens`}
        >
          <Typography owner="client" variant="body2">
            {item.stats?.unique_visitors ?? 0}
          </Typography>
        </Col>
        <Col lg={2} className="d-none d-lg-block">
          <Typography owner="client" variant="body2">
            {lastActivity}
          </Typography>
        </Col>
        <Col xs={1} className="d-flex justify-content-center">
          {actionButton("Copy share link", copyShareLink, ShareIcon, !shareLink)}
        </Col>
        <Col xs={1} className="d-flex justify-content-center">
          {actionButton(
            "Edit",
            () => navigate(`/sales-rooms/edit/${item.id}`),
            EditIcon
          )}
        </Col>
        <Col xs={1} className="d-flex justify-content-center">
          {actionButton(
            "Open buyer view",
            () => window.open(`${shareLink}?preview=1`, "_blank"),
            OpenNewIcon,
            !shareLink
          )}
        </Col>
      </Row>
    </Col>
  );
}
